import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WaitlistForm from "@/components/WaitlistForm";
import Countdown from "@/components/Countdown";
import { Reveal, MaskedLine, SectionLabel } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { scrollToId } from "@/scroll";

const FEATURES = [
  { n: "01", img: "/images/pack_angle.png", title: "Ballistic Shell", body: "A 1680D ballistic nylon shell — the same class of material used in military gear. Abrasion, tear and weather resistance engineered for decades of daily abuse." },
  { n: "02", img: "/images/pack_open.png", title: "Modular Storage", body: "A full clamshell opening with 43 purpose-built pockets. Vertical tool panels keep every tool visible, protected and exactly where you left it." },
  { n: "03", img: "/images/pack_harness.png", title: "Load-Bearing Harness", body: "A padded, contoured harness with breathable back channels and an adjustable sternum strap — engineered to carry 20 kg of steel all day without digging in." },
  { n: "04", img: "/images/pack_base.png", title: "Weatherproof Base", body: "A molded waterproof polymer base that stands upright on mud, concrete and wet floors. Your tools never touch the ground." },
];

const GALLERY = [
  { img: "/images/pack_front.png", label: "Front Profile" },
  { img: "/images/pack_angle.png", label: "Three-Quarter" },
  { img: "/images/pack_open.png", label: "Clamshell Interior" },
  { img: "/images/detail_logo.png", label: "Rubber CHWA Patch" },
  { img: "/images/detail_zipper.png", label: "Oversized Zippers" },
  { img: "/images/pack_harness.png", label: "Harness System" },
  { img: "/images/pack_base.png", label: "Molded Base" },
  { img: "/images/macro_stitching.png", label: "Bar-Tack Stitching" },
  { img: "/images/macro_fabric.png", label: "1680D Weave" },
  { img: "/images/macro_molle.png", label: "MOLLE Webbing" },
  { img: "/images/macro_handle.png", label: "Rubberized Grab Handle" },
  { img: "/images/macro_pocket.png", label: "Pocket Construction" },
  { img: "/images/int_loops.png", label: "Elastic Retention" },
  { img: "/images/int_lining.png", label: "Waterproof Lining" },
];

const POCKETS = [
  { img: "/images/int_drill_pocket.png", label: "Drill & Impact Driver Bays" },
  { img: "/images/int_handtools.png", label: "Pliers, Drivers & Tape" },
  { img: "/images/int_sockets.png", label: "Sockets & Accessories" },
  { img: "/images/int_dividers.png", label: "Modular Removable Dividers" },
];

const SPECS = [
  ["Dimensions", '22" H × 14" W × 9" D'],
  ["Capacity", "32 L"],
  ["Weight", "2.4 kg"],
  ["Shell", "1680D ballistic nylon"],
  ["Liner", "420D ripstop"],
  ["Zippers", "Oversized #10 coil, glove-friendly pulls"],
  ["Base", "Molded waterproof polymer"],
  ["Pockets", "43 total — 27 interior, 16 exterior"],
  ["Harness", "Padded load-bearing with sternum strap"],
  ["Laptop Sleeve", 'Fits up to 16"'],
  ["Warranty", "25 years, repair-first"],
];

const MATERIALS = [
  { n: "01", name: "1680D Ballistic Nylon", body: "Maximum abrasion and tear resistance. The shell shrugs off rebar, concrete and truck beds." },
  { n: "02", name: "#10 Oversized Zippers", body: "Rated for tens of thousands of cycles. Big enough to operate with winter gloves on." },
  { n: "03", name: "Debossed Rubber Patch", body: "The CHWA mark — molded, matte and permanent. It will outlast the bag itself." },
  { n: "04", name: "Molded Polymer Base", body: "Fully waterproof, impact-proof and self-standing. Sets down anywhere, wipes clean." },
  { n: "05", name: "Bar-Tack Reinforcement", body: "Every stress point is bar-tack stitched — the same reinforcement used in fall-protection gear." },
];

const FAQS = [
  { q: "When does the CHWA-01 ship?", a: "The CHWA-01 launches with our first production run. The countdown on this page always shows the current launch date — waitlist members get first-allocation access before the public release." },
  { q: "How much will it cost?", a: "Pricing is announced at launch. The CHWA-01 is positioned as professional equipment — built to be the last tool backpack you buy, not the cheapest one you replace." },
  { q: "What fits inside?", a: 'A full hand-tool kit: meters, drivers, wrenches, bits, a cordless drill, plus a 16" laptop sleeve and document pocket. 43 pockets keep everything vertical and visible.' },
  { q: "Is it waterproof?", a: "The molded base is fully waterproof and the ballistic shell is highly water-resistant. Rain, wet floors and truck beds are not a concern." },
  { q: "What does the warranty cover?", a: "A 25-year repair-first warranty covering stitching, zippers, base and harness. If it fails doing real work, we repair it. If it can't be repaired, we replace it." },
  { q: "Where is CHWA made?", a: "Designed and engineered at our Texas headquarters, manufactured with premium global production partners to our specification and inspected to our standard." },
];

export default function ProductPage() {
  const heroRef = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    document.title = "CHWA-01 — The Standard Pack | CHWA";
    window.__chwa_lenis?.scrollTo(0, { immediate: true });
  }, []);

  const goDealer = () => navigate("/", { state: { scrollTo: "#dealer" } });

  return (
    <>
      <Nav />
      <main>
        <section ref={heroRef} data-testid="product-hero" className="relative flex h-[100svh] min-h-[680px] items-end overflow-hidden">
          <motion.div className="absolute inset-0" style={{ y: bgY }}>
            <img src="/images/pack_front.png" alt="CHWA-01 Standard Pack front profile" className="h-full w-full scale-105 object-cover" data-testid="product-hero-image" />
            <div className="absolute inset-0 bg-black/50" />
            <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
          </motion.div>

          <div className="relative z-10 w-full px-6 pb-14 md:px-12">
            <MaskedLine delay={0.1}>
              <p className="text-xs uppercase tracking-[0.35em] text-zinc-400">The Flagship — Tool Backpacks</p>
            </MaskedLine>
            <MaskedLine delay={0.25}>
              <h1 data-testid="product-title" className="font-display text-[clamp(4rem,15vw,15rem)] font-bold uppercase leading-[0.82] tracking-tight text-zinc-50">
                CHWA-01
              </h1>
            </MaskedLine>
            <div className="mt-6 flex flex-wrap items-end justify-between gap-8">
              <MaskedLine delay={0.4}>
                <p className="font-display text-2xl uppercase tracking-[0.15em] text-zinc-300 sm:text-3xl">The Standard Pack.</p>
              </MaskedLine>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65 }} className="flex flex-wrap gap-4">
                <button data-testid="product-waitlist-cta" onClick={() => scrollToId("#waitlist")} className="bg-zinc-100 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-950 transition-colors duration-300 hover:bg-white">
                  Join the Waitlist
                </button>
                <button data-testid="product-dealer-cta" onClick={goDealer} className="border border-zinc-600 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-100 transition-colors duration-300 hover:border-zinc-200">
                  Become a Dealer
                </button>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="border-b border-zinc-800 px-6 py-16 md:px-12">
          <Countdown />
        </section>

        <section data-testid="product-features" className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-36">
          <Reveal>
            <SectionLabel index="01" title="Engineered Highlights" />
          </Reveal>
          <div className="mt-16 space-y-20 md:space-y-28">
            {FEATURES.map((f, i) => (
              <Reveal key={f.n} y={30}>
                <div data-testid={`product-feature-${f.n}`} className={`grid items-center gap-10 md:grid-cols-2 ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
                  <div className="clip-frame overflow-hidden border border-zinc-800">
                    <img src={f.img} alt={f.title} className="h-80 w-full object-cover transition-transform duration-700 hover:scale-[1.03] md:h-[480px]" />
                  </div>
                  <div className={i % 2 ? "md:pr-8" : "md:pl-8"}>
                    <span className="font-display text-6xl font-bold text-zinc-800 md:text-7xl">{f.n}</span>
                    <h3 className="mt-4 font-display text-3xl font-semibold uppercase tracking-tight text-zinc-50 sm:text-4xl lg:text-5xl">{f.title}</h3>
                    <p className="mt-5 max-w-md text-base leading-relaxed text-zinc-400 md:text-lg">{f.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section data-testid="product-gallery" className="border-t border-zinc-800 bg-[#0D0D0D]">
          <div className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-36">
            <Reveal>
              <SectionLabel index="02" title="Gallery" />
              <h2 className="mt-8 font-display text-4xl font-semibold uppercase tracking-tight text-zinc-50 sm:text-5xl lg:text-7xl">The Details.</h2>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {GALLERY.map((g, i) => (
                <Reveal key={g.img} delay={0.04 * i} y={20}>
                  <figure data-testid={`gallery-item-${i}`} className="group">
                    <div className="clip-frame overflow-hidden border border-zinc-800">
                      <img src={g.img} alt={g.label} className="h-72 w-full object-cover opacity-85 transition-[opacity,transform] duration-700 group-hover:scale-[1.04] group-hover:opacity-100" />
                    </div>
                    <figcaption className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">{g.label}</figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="product-organization" className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-36">
          <Reveal>
            <SectionLabel index="03" title="Organization" />
            <h2 className="mt-8 max-w-3xl font-display text-4xl font-semibold uppercase leading-[0.95] tracking-tight text-zinc-50 sm:text-5xl lg:text-7xl">
              Every tool.<br />Exactly where you left it.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-4 md:grid-cols-4">
            {[
              ["43", "Total Pockets"],
              ["27", "Interior Loops & Sleeves"],
              ["16", "Exterior Quick Access"],
              ["0", "Wasted Compartments"],
            ].map(([v, k], i) => (
              <Reveal key={k} delay={0.05 * i} y={16}>
                <div data-testid={`org-stat-${i}`} className="border border-zinc-800 px-6 py-10 text-center">
                  <p className="font-display text-5xl font-bold text-zinc-50 md:text-6xl">{v}</p>
                  <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">{k}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {[
              { img: "/images/int_clamshell_empty.png", label: "Empty — The Architecture", testid: "org-empty" },
              { img: "/images/int_loaded.png", label: "Loaded — Full Contractor Kit", testid: "org-loaded" },
            ].map((s) => (
              <Reveal key={s.img} y={24}>
                <figure data-testid={s.testid} className="group">
                  <div className="clip-frame overflow-hidden border border-zinc-800">
                    <img src={s.img} alt={s.label} className="h-80 w-full object-cover opacity-90 transition-[opacity,transform] duration-700 group-hover:scale-[1.03] group-hover:opacity-100 md:h-[480px]" />
                  </div>
                  <figcaption className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">{s.label}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {POCKETS.map((p, i) => (
              <Reveal key={p.img} delay={0.04 * i} y={20}>
                <figure data-testid={`pocket-${i}`} className="group">
                  <div className="clip-frame overflow-hidden border border-zinc-800">
                    <img src={p.img} alt={p.label} className="h-56 w-full object-cover opacity-85 transition-[opacity,transform] duration-700 group-hover:scale-[1.04] group-hover:opacity-100 md:h-64" />
                  </div>
                  <figcaption className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">{p.label}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </section>

        <section data-testid="product-materials" className="border-t border-zinc-800 bg-[#0D0D0D]">
          <div className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-36">
            <Reveal>
              <SectionLabel index="04" title="Materials" />
              <h2 className="mt-8 font-display text-4xl font-semibold uppercase tracking-tight text-zinc-50 sm:text-5xl lg:text-7xl">What It's Made Of.</h2>
            </Reveal>
            <div className="mt-14">
              {MATERIALS.map((m, i) => (
                <Reveal key={m.n} delay={0.03 * i} y={16}>
                  <div data-testid={`material-row-${m.n}`} className="grid items-baseline gap-3 border-t border-zinc-800 py-8 last:border-b md:grid-cols-12">
                    <span className="font-display text-2xl font-bold text-zinc-700 md:col-span-1">{m.n}</span>
                    <h3 className="font-display text-2xl font-semibold uppercase tracking-tight text-zinc-100 md:col-span-5 md:text-3xl">{m.name}</h3>
                    <p className="max-w-xl text-base leading-relaxed text-zinc-400 md:col-span-6">{m.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="product-specs" className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-36">
          <Reveal>
            <SectionLabel index="05" title="Technical Specifications" />
            <h2 className="mt-8 font-display text-4xl font-semibold uppercase tracking-tight text-zinc-50 sm:text-5xl lg:text-7xl">The Numbers.</h2>
          </Reveal>
          <Reveal y={20} className="mt-14">
            <div data-testid="specs-table" className="grid border border-zinc-800 md:grid-cols-2">
              {SPECS.map(([k, v], i) => (
                <div key={k} className={`flex items-baseline justify-between gap-6 border-zinc-800 px-6 py-5 max-md:border-b md:py-6 ${i % 2 === 0 ? "md:border-r" : ""} ${i < SPECS.length - (SPECS.length % 2 ? 1 : 2) ? "md:border-b" : ""}`}>
                  <span className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">{k}</span>
                  <span className="text-right font-display text-lg font-semibold uppercase text-zinc-100">{v}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        <section data-testid="product-warranty" className="border-t border-zinc-800 bg-[#0D0D0D]">
          <div className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-36">
            <div className="grid gap-12 md:grid-cols-2 md:items-center">
              <div>
                <Reveal>
                  <SectionLabel index="06" title="Warranty" />
                </Reveal>
                <MaskedLine delay={0.1} className="mt-8">
                  <h2 className="font-display text-5xl font-bold uppercase leading-[0.9] tracking-tight text-zinc-50 sm:text-6xl lg:text-8xl">25-Year<br />Warranty.</h2>
                </MaskedLine>
              </div>
              <Reveal delay={0.15}>
                <p className="text-base leading-relaxed text-zinc-400 md:text-lg">
                  If it fails doing real work, we repair it. If it can't be repaired, we replace it. Stitching, zippers, base and harness — covered for 25 years.
                </p>
                <p className="mt-5 text-sm leading-relaxed text-zinc-500">
                  We build the CHWA-01 to be the last tool backpack you buy. A warranty this long is not a marketing line — it is a design constraint.
                </p>
                <div className="mt-8 space-y-3 text-xs uppercase tracking-[0.25em] text-zinc-500">
                  <p data-testid="warranty-point-repair">— Repair first, replace when needed</p>
                  <p data-testid="warranty-point-coverage">— Stitching, zippers, base, harness</p>
                  <p data-testid="warranty-point-registration">— Warranty registration at launch</p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section data-testid="product-faq" className="mx-auto max-w-[1100px] px-6 py-28 md:px-12 md:py-36">
          <Reveal>
            <SectionLabel index="07" title="FAQ" />
            <h2 className="mt-8 font-display text-4xl font-semibold uppercase tracking-tight text-zinc-50 sm:text-5xl lg:text-6xl">Questions.</h2>
          </Reveal>
          <Reveal y={20} className="mt-12">
            <Accordion type="single" collapsible data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-zinc-800" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="py-6 text-left font-display text-lg font-semibold uppercase tracking-tight text-zinc-100 hover:text-zinc-50 hover:no-underline md:text-xl" data-testid={`faq-question-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-base leading-relaxed text-zinc-400" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </section>

        <section id="waitlist" data-testid="product-waitlist-section" className="border-t border-zinc-800 bg-[#0D0D0D]">
          <div className="mx-auto max-w-[1600px] px-6 py-28 text-center md:px-12 md:py-36">
            <MaskedLine>
              <h2 className="font-display text-4xl font-bold uppercase leading-[0.9] tracking-tight text-zinc-50 sm:text-5xl lg:text-7xl">Be First in Line.</h2>
            </MaskedLine>
            <Reveal delay={0.1}>
              <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-zinc-500">
                First-allocation access, launch pricing and production updates — before anyone else.
              </p>
              <div className="mt-10">
                <WaitlistForm source="chwa-01" />
              </div>
              <button data-testid="waitlist-dealer-link" onClick={goDealer} className="mt-6 text-xs uppercase tracking-[0.25em] text-zinc-500 underline-offset-4 transition-colors duration-300 hover:text-zinc-100 hover:underline">
                Selling to the trades? Become a Dealer
              </button>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
